// src/quality-management/components/EditProjectModal.tsx
// Configuração/edição de um projeto existente, reutilizando o formulário compartilhado

import React, { useState } from 'react';
import { Project, UpdateProjectRequest } from '../services/api/types';
import { useMutation } from '../hooks/useApi';
import { useEmployees, useManagers } from '../hooks/useDirectory';
import { apiService } from '../services/api';
import { Modal } from '../../shared/components/ui/Modal';
import { Button } from '../../shared/components/ui/Button';
import ProjectFormFields from './project-form/ProjectFormFields';
import { ProjectFormState, buildProjectPayload, projectToForm } from './project-form/projectForm';

interface EditProjectModalProps {
  project: Project;
  onClose: () => void;
  onProjectUpdated: (updated: Project) => void;
}

const FORM_ID = 'edit-project-form';

const EditProjectModal: React.FC<EditProjectModalProps> = ({ project, onClose, onProjectUpdated }) => {
  const [form, setForm] = useState<ProjectFormState>(() => projectToForm(project));
  const [error, setError] = useState('');

  const update = (patch: Partial<ProjectFormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const { data: employees, loading: loadingEmployees } = useEmployees();
  const { data: managers } = useManagers();

  const { mutate: updateProject, loading: isSubmitting } = useMutation(
    (data: UpdateProjectRequest) => apiService.projects.updateProject(project.id, data),
    {
      onSuccess: (updated) => onProjectUpdated(updated as Project),
      onError: (e) => setError(e.message || 'Erro ao salvar projeto. Tente novamente.'),
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Nome do projeto é obrigatório');
      return;
    }
    setError('');
    await updateProject(buildProjectPayload(form));
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title="Configurar projeto"
      description="Atualize os dados de gestão e a equipe responsável pelo projeto."
      size="xl"
      closeOnBackdrop={!isSubmitting}
      data-testid="edit-project-modal"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={isSubmitting}
            disabled={isSubmitting || !form.name.trim()}
            data-testid="project-update-save"
          >
            Salvar alterações
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <ProjectFormFields
          form={form}
          update={update}
          managers={managers || []}
          employees={employees || []}
          loadingEmployees={loadingEmployees}
          disabled={isSubmitting}
          error={error}
          defaultExpanded
        />
      </form>
    </Modal>
  );
};

export default EditProjectModal;
